import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface ApprovalStepDef {
  id?: string;
  stepOrder: number;
  name: string;
  approverType: 'role' | 'specific_user' | 'requester_line_manager' | 'requester_department_manager' | 'requester_manager_chain';
  approverRole?: string | null;
  approverUserId?: string | null;
  approverUserName?: string | null;
  slaHours?: number | null;
  allowReject?: boolean;
  allowClarify?: boolean;
  allowDefer?: boolean;
  allowTransferTopMgmt?: boolean;
  autoApproveIfRequesterIsApprover?: boolean;
}

export interface ApprovalChain {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  isDefault: boolean;
  isTopMgmtEscalation: boolean;
  steps: ApprovalStepDef[];
  _count?: { rules: number };
}

export function useApprovalChains() {
  const { toast } = useToast();
  const [chains, setChains] = useState<ApprovalChain[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/approval-chains');
      setChains(res.data.chains ?? []);
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Failed to load approval chains', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { refetch(); }, [refetch]);

  const createChain = async (data: Partial<ApprovalChain>) => {
    try {
      await api.post('/admin/approval-chains', data);
      toast('Approval chain created', 'success');
      await refetch();
      return true;
    } catch (e: any) {
      toast(e?.response?.data?.errors?.[0] || e?.response?.data?.error || 'Create failed', 'error');
      return false;
    }
  };

  const updateChain = async (id: string, data: Partial<ApprovalChain>) => {
    try {
      await api.put(`/admin/approval-chains/${id}`, data);
      toast('Approval chain updated', 'success');
      await refetch();
      return true;
    } catch (e: any) {
      toast(e?.response?.data?.errors?.[0] || e?.response?.data?.error || 'Update failed', 'error');
      return false;
    }
  };

  const deleteChain = async (id: string) => {
    try {
      await api.delete(`/admin/approval-chains/${id}`);
      toast('Approval chain removed', 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Delete failed', 'error');
    }
  };

  return { chains, loading, refetch, createChain, updateChain, deleteChain };
}
